'use client';

/**
 * Global error boundary — caught whenever any server component, page, or
 * layout inside the app throws during rendering (excluding the root layout
 * itself, which is handled by global-error.tsx).
 *
 * IMPORTANT: Never render `error.message` or `error.stack` here.
 * The full error is logged server-side by the framework; the user only sees
 * the digest reference ID which is opaque and reveals nothing about internals.
 */

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'motion/react';
import { RefreshCw, ArrowLeft } from 'lucide-react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // error.message / error.stack stay on the server — Next.js logs them
    // automatically; this console.error runs client-side only as a passthrough
    // for any wired browser error monitoring (e.g. Sentry, PostHog).
    // We intentionally do NOT display error.message to the user.
    console.error('[error-boundary]', error.digest ?? 'no-digest');
  }, [error]);

  const router = useRouter();

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background">
      {/* Subtle background accent — mirrors the not-found page style */}
      <div
        className="pointer-events-none absolute right-0 top-0 h-[80vh] w-[50vw] bg-gradient-to-br from-orange-500/5 to-red-500/5 dark:from-orange-500/10 dark:to-red-500/10"
        style={{ clipPath: 'polygon(30% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
      />

      {/* Main content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-between px-6 py-12 md:px-12 lg:px-16">

        {/* Top navigation hint */}
        <motion.div
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <span>ERROR</span>
          <span className="text-orange-500">/</span>
          <span>500</span>
        </motion.div>

        {/* Body */}
        <div className="flex flex-col gap-8 md:gap-12">
          <div className="relative -ml-2 md:-ml-4">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="font-playfair text-[7rem] font-black leading-[0.85] tracking-tighter text-foreground md:text-[11rem] lg:text-[14rem]">
                5<span className="text-orange-500">0</span>0
              </h1>

              {/* Decorative line */}
              <motion.div
                className="mt-6 h-1 w-32 bg-orange-500 md:w-48"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformOrigin: 'left' }}
              />
            </motion.div>
          </div>

          <motion.div
            className="ml-0 max-w-2xl md:ml-8 lg:ml-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <h2 className="mb-4 text-3xl font-light leading-tight text-foreground md:text-4xl lg:text-5xl">
              Something went wrong
            </h2>

            <p className="mb-2 text-muted-foreground">
              An unexpected error occurred. Please try again or return to the dashboard.
            </p>

            {/* Opaque digest — safe to show, reveals nothing about internals */}
            {error.digest && (
              <p className="mb-8 font-mono text-xs text-muted-foreground/50">
                Reference: {error.digest}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <motion.button
                onClick={reset}
                className="group relative overflow-hidden bg-foreground px-8 py-4 text-base font-semibold text-background transition-all hover:bg-foreground/90 flex items-center gap-3"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 flex items-center gap-3">
                  <RefreshCw className="h-5 w-5" />
                  Try again
                </span>
                <motion.div
                  className="absolute inset-0 bg-orange-500"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>

              <button
                onClick={() => router.push('/assessments')}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to dashboard
              </button>
            </div>
          </motion.div>
        </div>

        <div />
      </div>
    </div>
  );
}
